import { Link, useParams } from "react-router-dom";
import DashboardLayout from "../../../Layout/DashboardLayout";
import AddNote from "./Components/AddNote";
import ShippingInformation from "./Components/ShippingInformation";
import DashboardHeader from "../../../Components/DashboardHeader";
import { useOrder } from "hooks/OrderManagement/useOrders";
import { OrderStatusTimeline } from "./details/Stepper";
import Scaffold from "Components/Scaffold";
import { ellipse } from "utils/ellipse";
import { formatCurrency } from "utils/formatNumber";
import { Separator } from "Components/Separator";
import { Avatar } from "./Components/Avatar";
import { Status } from "./Components/Status";
import dayjs from "dayjs";
import Loader from "Components/Loader";
import { OrderDetailsItemAction } from "./details/OrderDetailsItemAction";
import { DeliveryType } from "types/deliveryType.enum";
import { DownloadReceipt } from "./details/DownloadReceipt";
import { StatusLogs } from "./details/StatusLogs";

const OrderDetails = () => {
  const params = useParams();
  const { data, isPending } = useOrder(params.id || "");
  console.log("🚀 ~ OrderDetails ~ data:", data);

  const order = data?.data;
  const orderProducts = order?.orderProducts || [];
  const status = data?.data.orderStatus;

  const date = dayjs(order?.createdAt || dayjs());
  const datePlaced = date.format("D MMMM YYYY, h:mm A");

  if (isPending) {
    return <Loader />;
  }

  if (!order) {
    return <div className="">No order with such ID</div>;
  }

  const isPickup = order.deliveryMethod === DeliveryType.SELF_DELIVERY;
  return (
    <DashboardLayout
      title="Order Management"
      back={false}
      className="h-full pb-10"
    >
      <DashboardHeader
        firstTitle={"Order Management"}
        secondTitle={"Order Details"}
      />
      <div className="ml-[20px] mt-[25px] mr-[14px]">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex gap-x-1">
              <h3 className="text-[18px]">Order ID: #{params.id}</h3>
              <Status value={status} className="text-xs" />
            </div>
            <p className="text-[#ababab]">Order Placed: {datePlaced}</p>
          </div>
          <div className="text-sm mr-2">
            Payment Status:{" "}
            <Status value={order.paymentStatus} className="text-sm" />
          </div>
          {/* <span className="flex gap-2">
            <select
              title="status"
              name=""
              id=""
              className="rounded-md bg-[#FFF5F0] text-orange px-[13px] py-[17px] flex gap-1">
              <option value="">Placed</option>
              <option value="">Delivered</option>
              <option value="">Processing</option>
            </select>
            <button className="rounded-md bg-[#F3F3F3] text-[#414141] px-[13px] py-[17px] flex gap-1">
              <LeftIcon />
              Prev
            </button>
            <button className="rounded-md bg-[#F3F3F3] text-[#414141] px-[13px] py-[17px] flex gap-1">
              Next <RightIcon />
            </button>
          </span> */}
        </div>
        <div className="mt-[30px] pb-10">
          <div className="flex gap-3">
            <div className="w-8/12 border-r-[1px] border-[#eee] pr-5 space-y-4">
              <OrderStatusTimeline
                status={status}
                deliveryMethod={order?.deliveryMethod}
              />

              <Scaffold left="Item Ordered" className="p-0">
                {orderProducts.map((item: any) => (
                  <div
                    className="flex justify-between items-center text-base font-medium p-4 gap-x-2"
                    key={item.id}
                  >
                    <div className="size-20">
                      <img
                        src={item.image}
                        title={item.productName}
                        alt={item.productName}
                        className="object-contain size-20"
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-2">
                      <div className="flex-1 flex items-center justify-between gap-2">
                        <div className="flex flex-col justify-between">
                          <p className="font-medium">
                            {ellipse(item.productName)} ({item?.measurement})
                          </p>
                          <p className="font-medium mt-2 text-[12px]">
                            Quantity: {item.unit}X
                          </p>
                          {item.recommendedUnit && (
                            <p className="font-medium text-[12px]">
                              Recommended Quantity:{" "}
                              {item.recommendedUnit
                                ? `${item.recommendedUnit}X`
                                : "n/a"}
                            </p>
                          )}
                        </div>
                        <div className="flex justify-between">
                          <div className="text-white bg-grey font-semibold py-4 px-5 rounded-xl">
                            {formatCurrency(item.totalAmount / 100)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <Separator />

                <OrderDetailsItemAction data={order} />
              </Scaffold>
              <Scaffold left="Order Summary" className="p-0">
                <div className="grid gap-6 py-7 px-4">
                  <div className="grid grid-cols-3">
                    <div className="">Payment Method</div>
                    <div className="text-center"></div>
                    <div className="text-right">{order?.paymentMethod}</div>
                  </div>
                  <div className="grid grid-cols-3">
                    <div className="">Shipping</div>
                    <div className="text-center">{isPickup ? "Free" : "-"}</div>
                    <div className="text-right">{order?.deliveryMethod}</div>
                  </div>
                  <div className="grid grid-cols-3">
                    <div className="">Type</div>
                    <div className="text-center"></div>
                    <div className="text-right capitalize">
                      {order.productOrderType}
                    </div>
                  </div>
                  <div className="grid grid-cols-3">
                    <div className="">Subtotal</div>
                    <div className="text-center">
                      {order?.orderProducts.length} items
                    </div>
                    <div className="text-right">
                      {formatCurrency(order?.totalProductsAmount / 100)}
                    </div>
                  </div>
                  <div className="grid grid-cols-3">
                    <div className="">Discount</div>
                    <div className="text-center">{"-"}</div>
                    <div className="text-right">{order?.discountAmount}</div>
                  </div>

                  <div className="grid grid-cols-3">
                    <div className="">Total</div>
                    <div className="text-center"></div>
                    <div className="text-right font-bold capitalize">
                      {formatCurrency(order?.totalProductsAmount / 100)}
                    </div>
                  </div>
                </div>
                <Separator />

                <div className="flex justify-between p-4 items-center">
                  <p className="text-primary-foreground">
                    You have to accept this order to perform this action
                  </p>
                  <DownloadReceipt data={order} />
                </div>
              </Scaffold>
            </div>

            <div className="w-4/12">
              <div className="border-[1px] border-[#eee] rounded-md mb-[15px]">
                <p className="border-b-[1px] border-[#eee] py-[20px] px-[15px] font-medium">
                  Customer
                </p>
                <div className="flex gap-3 py-[20px] px-[15px] items-center">
                  <Avatar fallback={order?.customerName} />
                  <div className="flex-1 ">
                    <div className="flex justify-between items-center">
                      <div className="">
                        <h3>{order?.customerName}</h3>
                        {order?.customerEmail && (
                          <p className="text-[#ababab]">
                            {order?.customerEmail}
                          </p>
                        )}
                      </div>
                      <Link
                        to={`/customer-management/${order?.customerId}`}
                        target="_blank"
                      >
                        <LinkIcon />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {order?.deliveryDriver && (
                <div className="border-[1px] border-[#eee] rounded-md mb-[15px]">
                  <p className="border-b-[1px] border-[#eee] py-[20px] px-[15px] font-medium">
                    Driver
                  </p>
                  <div className="flex gap-3 py-[20px] px-[15px] items-center">
                    <Avatar fallback={order?.deliveryDriver?.firstName} />
                    <div className="flex-1 ">
                      <div className="flex justify-between items-center">
                        <div className="">
                          <h3>
                            {order?.deliveryDriver?.firstName}{" "}
                            {order?.deliveryDriver?.lastName}
                          </h3>
                          {order?.deliveryDriver?.email && (
                            <p className="text-[#ababab]">
                              {order?.deliveryDriver?.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <ShippingInformation data={order} />
              <AddNote data={order} />
              <StatusLogs data={order} />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OrderDetails;

const RightIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m8.25 4.5 7.5 7.5-7.5 7.5"
      />
    </svg>
  );
};
const LeftIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 19.5 8.25 12l7.5-7.5"
      />
    </svg>
  );
};

const LinkIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
};
